import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { SuccessDialog } from "./SuccessDialog";
import { ArrowLeft, Loader2, Pencil, Save } from "lucide-react";
import { API, authHdr, Field, pairFields } from "./AssociateFields";
import { EMP_TABS, EMP_TITLES, empFields, validateEmp } from "./employeeSpec";
import { RightsTab } from "./RightsTab";
import { EmployeeDownloads } from "./EmployeeDownloads";

const EDITABLE = ["Employee Creation Pending", "Pending for Superuser Verification", "Rejected By Superuser", "Rejected By Employee", "Active", "Inactive"];
const teal = "bg-[#1a7f6f] hover:bg-[#166a5c]";

const Sections = ({ steps, draft, setStep, ro, errs, ctx }) => (
  <div className="space-y-8">{steps.map((s) => (
    <section key={s} className="space-y-3" data-testid={`employee-step-${s}`}>
      <h3 className="text-xs font-bold uppercase tracking-wider text-teal-700">{EMP_TITLES[s]}</h3>
      <div className="grid grid-cols-1 gap-x-4 gap-y-5 md:grid-cols-3">
        {pairFields(empFields(s, ctx)).map(({ fl, attach }) => (
          <Field key={fl.key} fl={fl} attach={attach} data={draft[s] || {}} set={(p) => setStep(s, p)} ro={ro} errs={errs[s] || {}} ctx={ctx} />))}
      </div>
    </section>))}
  </div>
);

export default function EmployeeWizard({ selfVerify = false }) {
  const { id: routeId, token } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [associates, setAssociates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState({});
  const [errs, setErrs] = useState({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reject, setReject] = useState({ open: false, reason: "" });
  const [done, setDone] = useState(null);

  const base = selfVerify ? `${API}/employee-verify/${token}` : `${API}/employees/${routeId}`;
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(base, { headers: selfVerify ? {} : authHdr() });
        setDoc(r.data); setDraft(r.data.profile || {});
        if (!selfVerify) {
          const a = await axios.get(`${API}/associates/list`, { headers: authHdr() });
          setAssociates(a.data.associates.filter((x) => x.code && x.code !== "Junk"));
          const em = await axios.get(`${API}/employees/list`, { headers: authHdr() });
          setEmployees(em.data.employees.filter((x) => x.id !== routeId));
        }
      } catch (e) { toast.error(e.response?.data?.detail || "Could not load employee"); }
    })();
  }, [base]); // eslint-disable-line

  if (!doc) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const status = doc.status;
  const first = status === "Employee Creation Pending";
  const mode = selfVerify ? "self" : sp.get("mode") || (first ? "edit" : "view");
  const canEdit = !selfVerify && mode !== "verify" && EDITABLE.includes(status);
  const ro = !(canEdit && (first || editing || mode === "edit"));
  const tabs = EMP_TABS.filter((t) => !t.custom || !selfVerify);
  const tab = tabs[idx];
  const last = idx === tabs.length - 1;
  const ctx = { profile: draft, associates, employees, id: doc.id, token, base: `${API}/employees/${doc.id}`, publicBase: `${API}/employee-verify/${token}` };
  ctx.onIfsc = async (key, val) => {
    try {
      const r = await axios.get(`${API}/associates/lookup/ifsc/${val}`, { headers: authHdr() });
      setStep("bank", { bank_name: r.data.bank_name, branch: r.data.branch, micr: r.data.micr });
    } catch (e) { toast.error("IFSC not found"); }
  };
  function setStep(s, patch) {
    setDraft((d) => ({ ...d, [s]: { ...(d[s] || {}), ...patch } }));
    setErrs((e) => ({ ...e, [s]: Object.fromEntries(Object.entries(e[s] || {}).filter(([k]) => !(k in patch))) }));
  }
  const saveTab = async () => {
    if (ro || tab.custom) return true;
    const all = {};
    tab.steps.forEach((s) => { const e = validateEmp(s, draft[s] || {}, ctx); if (Object.keys(e).length) all[s] = e; });
    setErrs(all);
    if (Object.keys(all).length) { toast.error("Please fix the highlighted fields"); return false; }
    setBusy(true);
    try {
      let code = doc.employee_code;
      for (const s of tab.steps) code = (await axios.put(`${base}/step/${s}`, draft[s] || {}, { headers: authHdr() })).data.employee_code || code;
      setDoc((d) => ({ ...d, employee_code: code, profile: { ...d.profile, ...Object.fromEntries(tab.steps.map((s) => [s, draft[s] || {}])) } }));
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); return false; } finally { setBusy(false); }
  };
  const go = async (i) => { if (i > idx && !(await saveTab())) return; setIdx(i); window.scrollTo(0, 0); };
  const act = async (url, body) => {
    if (!(await saveTab())) return;
    setBusy(true);
    try { setDone((await axios.post(url, body || {}, { headers: selfVerify ? {} : authHdr() })).data.message || "Done"); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  const verify = mode === "verify" && status === "Pending for Superuser Verification";
  const selfMode = selfVerify && doc.can_act;
  const name = draft.details?.name || "New Employee";

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="employee-wizard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!selfVerify && <button onClick={() => nav("/sub-brokers/list?tab=employee")} aria-label="Back" data-testid="employee-back-header"
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 transition-colors hover:bg-gray-100"><ArrowLeft size={16} /></button>}
          <div>
            <h1 className="text-xl font-bold text-gray-900">{selfVerify ? "Verify your details" : first ? "New Employee" : ro ? "View Employee" : "Edit Employee"}</h1>
            <p className="mt-0.5 text-xs text-gray-500">{tab.label}{!first ? ` — ${name}` : ""}
              {doc.employee_code && <span className="ml-2 rounded bg-teal-50 px-1.5 py-0.5 font-mono text-teal-800" data-testid="employee-code">{doc.employee_code}</span>}</p>
          </div>
        </div>
        <Badge variant="outline" className="border-teal-200 bg-teal-50 text-teal-800" data-testid="employee-status-badge">{status}</Badge>
      </div>
      <Card className="border-gray-200 shadow-sm"><CardContent className="p-4 md:p-6">
        <div className="mb-6 flex items-end justify-between gap-2 border-b">
          <div className="-mb-px flex gap-1 overflow-x-auto" role="tablist">{tabs.map((t, i) => (
            <button key={t.key} type="button" role="tab" disabled={busy || (first && i > idx + 1)} onClick={() => go(i)} data-testid={`employee-tab-${t.key}`}
              className={`shrink-0 border-b-2 px-4 py-2.5 text-sm transition-colors ${i === idx ? "border-teal-700 font-semibold text-teal-700" : "border-transparent text-gray-500 hover:text-gray-800 disabled:opacity-40"}`}>{t.label}</button>))}
          </div>
          {canEdit && ro && !first && <Button size="sm" variant="outline" className="mb-2" onClick={() => setEditing(true)} data-testid="employee-edit-btn"><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>}
        </div>
        {tab.key === "rights" && (doc.user_id
          ? <RightsTab id={doc.associate_id} ro={ro} employeeId={doc.user_id} />
          : <p className="text-sm text-gray-500" data-testid="employee-rights-pending">Rights can be set once the employee has approved their details and their login is created.</p>)}
        {tab.key === "downloads" && <EmployeeDownloads doc={doc} />}
        {!tab.custom && <Sections steps={tab.steps} draft={draft} setStep={setStep} ro={ro} errs={errs} ctx={ctx} />}
        <div className="mt-8 flex flex-wrap justify-between gap-2 border-t pt-4">
          <Button variant="outline" disabled={idx === 0 || busy} onClick={() => go(idx - 1)} data-testid="employee-back-btn">Back</Button>
          <div className="flex flex-wrap gap-2">
            {!ro && !tab.custom && <Button variant="outline" disabled={busy} onClick={async () => (await saveTab()) && toast.success("Saved")} data-testid="employee-save-btn"><Save className="mr-1.5 h-4 w-4" />Save</Button>}
            {!last && <Button className={`${teal} px-8`} disabled={busy} onClick={() => go(idx + 1)} data-testid="employee-next-btn">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{ro ? "Next" : "Save & Continue"}</Button>}
            {last && (verify || selfMode) && <>
              <Button variant="destructive" disabled={busy} onClick={() => setReject({ open: true, reason: "" })} data-testid="employee-reject-btn">REJECT</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act(`${base}/${selfVerify ? "accept" : "su-accept"}`)} data-testid="employee-accept-btn">ACCEPT</Button></>}
            {last && !verify && !selfMode && ["Employee Creation Pending", "Rejected By Superuser", "Rejected By Employee"].includes(status) &&
              <Button className={`${teal} px-8`} disabled={busy} onClick={() => act(`${base}/submit`)} data-testid="employee-submit-btn">Submit</Button>}
            {last && !verify && !selfMode && !["Employee Creation Pending", "Rejected By Superuser", "Rejected By Employee"].includes(status) &&
              <Button className={`${teal} px-8`} onClick={() => (selfVerify ? setDone("Thank you.") : nav("/sub-brokers/list?tab=employee"))} data-testid="employee-finish-btn">Finish</Button>}
          </div>
        </div>
      </CardContent></Card>
      <Dialog open={reject.open} onOpenChange={(o) => setReject((r) => ({ ...r, open: o }))}>
        <DialogContent><DialogHeader><DialogTitle>Reason to Reject</DialogTitle></DialogHeader>
          <Textarea value={reject.reason} onChange={(e) => setReject((r) => ({ ...r, reason: e.target.value }))} data-testid="employee-reject-reason" />
          <DialogFooter><Button disabled={!reject.reason.trim()} data-testid="employee-reject-submit"
            onClick={() => { setReject({ open: false, reason: reject.reason }); act(`${base}/${selfVerify ? "reject" : "su-reject"}`, { reason: reject.reason }); }}>Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <SuccessDialog message={done} kind="Employee" testid="employee-success-dialog"
        onClose={() => { setDone(null); if (!selfVerify) nav("/sub-brokers/list?tab=employee"); }} />
    </div>
  );
}
